import styled, { keyframes } from 'styled-components';
import { ActivityType, getFormattedActivity } from '../utils';
import { Slide } from './types';

interface SlideRendererProps {
  slide: Slide;
  slideKey?: string;
}

/** Sentences need smaller font than single words. */
function isSentenceActivity(type?: ActivityType): boolean {
  return (
    type === ActivityType.SENTENCE_READING ||
    type === ActivityType.SENTENCE_DICTATION
  );
}

export default function SlideRenderer({ slide, slideKey }: SlideRendererProps) {
  switch (slide.type) {
    case 'activity-title': {
      const activityLabel = slide.activityType
        ? getFormattedActivity(slide.activityType)
        : slide.heading;
      return (
        <SlideContainer>
          <TitleBanner>
            <TitleHeading>{activityLabel}</TitleHeading>
          </TitleBanner>
          {slide.subheading && (
            <ActivitySkill>{slide.subheading}</ActivitySkill>
          )}
          {slide.icon && <ActivityIcon src={slide.icon} alt="" />}
        </SlideContainer>
      );
    }

    case 'content': {
      const isWordChain =
        slide.activityType === ActivityType.DECODING_WORD_CHAIN;
      const isWordSum = slide.activityType === ActivityType.WORD_SUMS;
      const isSentence = isSentenceActivity(slide.activityType);

      return (
        <SlideContainer>
          {slide.activityType && (
            <ContentHeader>
              {slide.icon && <SmallIcon src={slide.icon} alt="" />}
              <ContentActivityLabel>
                {getFormattedActivity(slide.activityType)}
              </ContentActivityLabel>
            </ContentHeader>
          )}
          <FadeIn key={slideKey}>
            {isWordChain && <ChainArrow>&#x25B6;</ChainArrow>}
            {slide.image ? (
              <SideBySide>
                <ContentText $small={isSentence} $mono={isWordSum}>
                  {slide.heading}
                </ContentText>
                <ImageGroup>
                  <ContentImage src={slide.image} alt={slide.subtext || ''} />
                  {slide.subtext && (
                    <ContentSubtext>{slide.subtext}</ContentSubtext>
                  )}
                </ImageGroup>
              </SideBySide>
            ) : (
              <ContentText $small={isSentence} $mono={isWordSum}>
                {slide.heading}
              </ContentText>
            )}
          </FadeIn>
        </SlideContainer>
      );
    }

    case 'word-matrix': {
      const webData = slide.wordMatrixData;
      if (!webData) return null;
      return (
        <SlideContainer>
          {slide.activityType && (
            <ContentHeader>
              {slide.icon && <SmallIcon src={slide.icon} alt="" />}
              <ContentActivityLabel>
                {getFormattedActivity(slide.activityType)}
              </ContentActivityLabel>
            </ContentHeader>
          )}
          <FadeIn key={slideKey}>
            <WordMatrixGrid
              $prefixCols={webData.prefixColumns.length}
              $suffixCols={webData.suffixColumns.length}
            >
              {webData.prefixColumns.map((col, ci) => (
                <WordMatrixPrefixColumn key={ci} $colIndex={ci}>
                  {col.map((p, i) => (
                    <WordMatrixCell key={i}>{p}</WordMatrixCell>
                  ))}
                </WordMatrixPrefixColumn>
              ))}
              <WordMatrixBase $colStart={webData.prefixColumns.length + 1}>
                {webData.baseWord}
              </WordMatrixBase>
              {webData.suffixColumns.map((col, ci) => (
                <WordMatrixSuffixColumn
                  key={ci}
                  $colIndex={webData.prefixColumns.length + 2 + ci}
                >
                  {col.map((s, i) => (
                    <WordMatrixCell key={i}>{s}</WordMatrixCell>
                  ))}
                </WordMatrixSuffixColumn>
              ))}
            </WordMatrixGrid>
            <WordMatrixInstruction>{slide.heading}</WordMatrixInstruction>
          </FadeIn>
        </SlideContainer>
      );
    }

    case 'refer-to-plan':
      return (
        <SlideContainer>
          {slide.activityType && (
            <ContentHeader>
              {slide.icon && <SmallIcon src={slide.icon} alt="" />}
              <ContentActivityLabel>
                {getFormattedActivity(slide.activityType)}
              </ContentActivityLabel>
            </ContentHeader>
          )}
          <ReferText>Refer to your lesson plan</ReferText>
          {slide.script && (
            <ScriptText>&ldquo;{slide.script}&rdquo;</ScriptText>
          )}
        </SlideContainer>
      );

    default:
      return null;
  }
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const FadeIn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeIn} 0.35s ease-out;
`;

const SlideContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  position: relative;
  overflow: hidden;
`;

const TitleBanner = styled.div`
  width: 100%;
  padding: 40px 5%;
  background-color: #3498db;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleHeading = styled.h1`
  font-size: 54px;
  font-weight: 700;
  color: white;
  text-align: center;
  margin: 0;
`;

const ActivitySkill = styled.p`
  font-size: 20px;
  color: #666;
  margin-top: 24px;
`;

const ActivityIcon = styled.img`
  width: 80px;
  height: 80px;
  margin-top: 32px;
  object-fit: contain;
`;

const ContentHeader = styled.div`
  position: absolute;
  top: 24px;
  left: 32px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SmallIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const ContentActivityLabel = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

const ContentText = styled.p<{ $small?: boolean; $mono?: boolean }>`
  font-size: ${(p) => (p.$small ? '48px' : '72px')};
  font-weight: 700;
  color: #000;
  text-align: center;
  margin: 0;
  padding: 0 10%;
  font-family: ${(p) =>
    p.$mono
      ? 'monospace'
      : "'SACursive', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"};
`;

const SideBySide = styled.div`
  display: flex;
  align-items: center;
  gap: 48px;
`;

const ImageGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentImage = styled.img`
  max-width: 200px;
  max-height: 200px;
  object-fit: contain;
`;

const ContentSubtext = styled.p`
  font-size: 28px;
  color: #666;
  margin-top: 12px;
  font-weight: 500;
`;

const ChainArrow = styled.span`
  font-size: 28px;
  color: #3498db;
  margin-bottom: 8px;
`;

const ReferText = styled.p`
  font-size: 32px;
  color: #666;
  text-align: center;
  font-style: italic;
`;

const ScriptText = styled.p`
  font-size: 20px;
  color: #999;
  text-align: center;
  margin-top: 24px;
  padding: 0 15%;
  font-style: italic;
`;

const WordMatrixGrid = styled.div<{ $prefixCols: number; $suffixCols: number }>`
  display: grid;
  grid-template-columns: ${(p) =>
    `${Array(p.$prefixCols).fill('auto').join(' ')} 1fr ${Array(p.$suffixCols).fill('auto').join(' ')}`};
  align-items: stretch;
  border: 3px solid #000;
  min-width: 400px;
`;

const WordMatrixPrefixColumn = styled.div<{ $colIndex: number }>`
  display: flex;
  flex-direction: column;
  grid-column: ${(p) => p.$colIndex + 1};
  ${(p) => p.$colIndex > 0 && 'border-left: 1px solid #000;'}
`;

const WordMatrixSuffixColumn = styled.div<{ $colIndex: number }>`
  display: flex;
  flex-direction: column;
  grid-column: ${(p) => p.$colIndex};
  border-left: 1px solid #000;
`;

const WordMatrixCell = styled.div`
  font-size: 32px;
  font-weight: 500;
  padding: 12px 24px;
  border-bottom: 1px solid #000;
  display: flex;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`;

const WordMatrixBase = styled.div<{ $colStart: number }>`
  grid-column: ${(p) => p.$colStart};
  grid-row: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 64px;
  font-weight: 700;
  padding: 24px 48px;
  border-left: 3px solid #000;
  border-right: 3px solid #000;
`;

const WordMatrixInstruction = styled.p`
  font-size: 24px;
  color: #666;
  text-align: center;
  margin-top: 40px;
  font-style: italic;
`;
